import { types } from './types';
import axios from 'axios';
import { executeCarefully } from '../../../../utilities/basicUtilities';
import { downloadFileRoot, fileToBase64 } from '../../../../utilities/fileUtilities';
import appOperations from '../../../../App/store/operations';
import isEmpty from 'lodash.isempty';
import { trackEvent, AnalyticsConstants } from '../../../../utilities/analyticsUtilities';
import { getPublicVideoLink } from '../ExportsPageHelpers';
import { SocialPlatformNames } from '../ExportsPageConstants';
import { history } from '../../../../App/history';
import Cookies from 'js-cookie';
import ColoredSnackbarConstants from '../../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import { EXPORT_STATUSES } from '../../../../constants/ExportStatuses';
import api from '../../../../App/api';

// DUMB ACTIONS

const setProperty = (key, value) => {
    return {
        type: types.SET_PROPERTY,
        key: key,
        value: value
    }
}

const removeExportFromListAction = (exportId) => {
    return {
        type: types.REMOVE_EXPORT_FROM_LIST,
        exportId
    }
}

const setExportDeletingProgress = (exportId, isDeleting) => {
    return {
        type: types.SET_EXPORT_DELETING_PROGRESS,
        exportId,
        isDeleting
    }
}

const setExportStatus = (exportId, status, videoFileUrl, rendererFailureCode) => {
    return {
        type: types.SET_EXPORT_STATUS,
        exportId,
        status,
        videoFileUrl,
        rendererFailureCode
    }
}

const updateExportProgress = (exportId, progress) => {
    return {
        type: types.UPDATE_EXPORT_PROGRESS,
        exportId: exportId,
        progress: progress
    }
}

const updateExportsPositions = (queuePositions) => {
    return {
        type: types.UPDATE_EXPORTS_POSITIONS,
        queuePositions: queuePositions,
    }
}

// SMART OPERATIONS

const removeExportFromList = (exportId) => {
    return (dispatch, getState) => {
        const state = getState();
        const videoExports = state.exports.videoExports;

        // Redirect to videos page if there is one last export to remove and we're on the exports page
        if (videoExports && videoExports.length <= 1 && history.location.pathname.includes('/exports')) {
            history.replace('/videos');
        }

        dispatch(removeExportFromListAction(exportId));
    }
}

const removeExportFromListWithRedirect = (exportId, exportStatus, navigate) => {
    return (dispatch, getState) => {
        const state = getState();
        const videoExports = state.exports.videoExports;
  
        // Redirect to videos page if there is one last export to remove and we're on the exports page
        if (videoExports && videoExports.length <= 1) {
            navigate('/videos', { replace: true })
        } else {
            if (exportStatus === EXPORT_STATUSES.PENDING) 
                dispatch(loadQueuePositions());

            dispatch(removeExportFromListAction(exportId));
        }

        dispatch(appOperations.openSnack('Export deleted.', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
    }
}

const loadQueuePositions = () => {
    return (dispatch) => {
        axios.get(`api/export/GetVideoExportsPositions`).then(response => {
            if (response.data.queuePositions && response.data.queuePositions.length !== 0)
                dispatch(updateExportsPositions(response.data.queuePositions));
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => loadQueuePositions(), 'Video exports positions not found.'));
        })
    }
}

const loadVideoExports = (videoId, callback) => {
    return (dispatch, getState) => {
        const state = getState();
        const pevSelectedExport = state.exports.selectedExport;

        dispatch(setProperty('isExportsDataLoading', true));

        api.get(`api/export/GetVideoExports/${videoId}`).then(response => {
            dispatch(setProperty('projectName', response.data.projectName));
            dispatch(setProperty('videoInfo', response.data.videoInfo));
            
            // Attach the additional fields for client usage
            if (!isEmpty(response.data.videoExports)) {
                response.data.videoExports.forEach((item) => {
                    item.isDeleting = false;
                });
            }

            dispatch(setProperty('videoExports', response.data.videoExports));
            
            const newSelectedExportData = response.data.videoExports.find(x=> x.exportId === pevSelectedExport?.exportId);
            
            if (newSelectedExportData) {
                dispatch(setProperty('selectedExport', newSelectedExportData));
            }

            if (callback) {
                executeCarefully(callback, response.data.videoExports);
            }
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => loadVideoExports(videoId, callback), 'Video not found.'));
        }).finally(() => {
            dispatch(setProperty('isExportsDataLoading', false));
        });
    }
}

const downloadVideoFile = (exportData, projectName, videoSource) => {
    return (dispatch) => {
        axios.get('api/export/GetExportDownloadId/' + exportData.exportId).then(response => {
            const url = `api/export/DownloadExport/${response.data.downloadId}`;
            const name = `Specterr_${exportData.resolution}p_${projectName.split('.')[0]}.mp4`;
            downloadFileRoot(url, name);
        }).catch(error => {
            const message = 'Could not download the video. Reload the page or try again later';
            dispatch(appOperations.failRequest(error, () => downloadVideoFile(exportData, projectName, videoSource), message));
        });
    }
}

const deleteVideoExport = (exportData, callback, navigate) => {
    return (dispatch) => {
        dispatch(setExportDeletingProgress(exportData.exportId, true));
        const jobId = exportData?.jobId;

        axios.post(`api/export/DeleteVideoExport/${exportData.exportId}`, { jobId }).then(() => {
            dispatch(removeExportFromListWithRedirect(exportData.exportId, exportData.status, navigate));

            if (exportData.status == EXPORT_STATUSES.RUNNING) {
                trackEvent('Aborted Export', {
                    videoType: exportData.type,
                    exportId: exportData.exportId,
                    category: AnalyticsConstants.VIDEO_CATEGORY,
                });
            }

            trackEvent('Deleted Export', {
                videoType: exportData.type,
                videoId: exportData.videoId,
                exportId: exportData.exportId,
                category: AnalyticsConstants.VIDEO_CATEGORY,
            });
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => deleteVideoExport(exportData, callback, navigate), 'Export not found.'));
        }).finally(() => {
            dispatch(setExportDeletingProgress(exportData.exportId, false));
            executeCarefully(callback);
        });
    }
}

const changeVideoPublicStatus = (videoId, status, callback) => {
    return (dispatch) => {
        dispatch(setProperty('videoInfo.public', status));
        var body = { videoId, status };

        axios.post('api/Video/SetPublicStatus', body).then(response => {
            executeCarefully(callback, status);

            if (status) {
                trackEvent('Set Video Public', {
                    category: AnalyticsConstants.SOCIAL_CATEGORY,
                    videoId: videoId
                });
            } else {
                trackEvent('Set Video Private', {
                    category: AnalyticsConstants.SOCIAL_CATEGORY,
                    videoId: videoId
                });
            }
        }).catch(error => {
            dispatch(setProperty('videoInfo.public', !status));
            dispatch(appOperations.failRequest(error, () => changeVideoPublicStatus(videoId, status, callback), 'Video not found.'));
        });
    }
}

const sharePublicVideo = (socialNetwork, exportId, videoId, isVideoPublic) => {
    return (dispatch) => {
        trackEvent('Clicked Social Share', {
            category: AnalyticsConstants.SOCIAL_CATEGORY,
            videoId: videoId,
            exportId: exportId,
            network: socialNetwork
        });

        if (socialNetwork === SocialPlatformNames.YOUTUBE) {
            axios.get('api/YouTube/Authorize').then(response => {
                if (response.data.authorized) {
                    dispatch(setProperty('youTubeUploadDialogOpen', true));
                } else if (Cookies.get(response.data.cookieKey)) {
                    dispatch(setProperty('googleAuthDialogOpen', true));
                    dispatch(setProperty('googleAuthUrl', response.data.authUrl));
                } else {
                    dispatch(appOperations.openSnack('Unable able to set cookies needed for YouTube login.', ColoredSnackbarConstants.SNACKBAR_ERROR));
                }
            }).catch(error => {
                dispatch(appOperations.failRequest(error, () => sharePublicVideo(socialNetwork, exportId, videoId, isVideoPublic), 'Can\'t connect to YouTube.'));
            });
            
            return;
        }

        let windowOptions = "toolbar=no,location=0,status=no,menubar=no,scrollbars=yes,width=700,height=385,resizable=1";
        var shareUrl = undefined;

        // Determine the options for social sharing
        if (socialNetwork == SocialPlatformNames.TWITTER) {
            let text = encodeURIComponent('Check out this video I made for my music.');
            let params = `url=${getPublicVideoLink(exportId)}&text=${text}&hashtags=specterr&via=specterrapp`;
            shareUrl = `https://twitter.com/intent/tweet?${params}`;
        } else if (SocialPlatformNames.FACEBOOK) {
            shareUrl = `https://www.facebook.com/dialog/feed?app_id=1126959061037011&display=popup&link=${getPublicVideoLink(exportId)}`;
        } else {
            return;
        }
        
        // Open the social network window
        if (isVideoPublic) {
            window.open(shareUrl, socialNetwork, windowOptions);
        } else {
            // Enable the sharing if the video is private
            dispatch(changeVideoPublicStatus(videoId, true, (status) => {
                if (status) {
                    window.open(shareUrl, socialNetwork, windowOptions);
                }
            }));
        }
    }
};

const manageVideoPreset = (presetName, imageFile , isEnabled, isPremium, selectedPresetId) => {
    return async  (dispatch, getState) => {
        const state = getState();
        const fileExtention = imageFile && imageFile.name.split('.').pop();

        if (imageFile)
            imageFile = await fileToBase64(imageFile);

        if (state.exports.selectedExport.status !== EXPORT_STATUSES.COMPLETED)
            dispatch(appOperations.openSnack('Unable to create preset from unfinished video.', ColoredSnackbarConstants.SNACKBAR_WARNING));

        const parameters = {
            exportId: state.exports.selectedExport.exportId,
            thumbnailImageFile: imageFile,
            thumbnailImageFileExtension: fileExtention,
            selectedPresetId: selectedPresetId,
            isEnabled,
            isPremium,
            presetName
        };

        dispatch(setProperty('isVideoPresetCreating', true));

        axios.post('api/VideoPresets/ManageVideoPreset', parameters).then((response) => { 
            dispatch(setProperty('manageVideoPresetDialogOpen', false));
            dispatch(appOperations.openSnack(response.data.message, ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        }).catch((error) => {
            const errorMessage = error?.response?.data?.message ?? "Unable create the video preset";
            dispatch(appOperations.openSnack(errorMessage, ColoredSnackbarConstants.SNACKBAR_ERROR));
        }).finally(() => {
            dispatch(setProperty('isVideoPresetCreating', false));
        })
    }
}

const closeYouTubeUploadDialog = () => {
    return (dispatch) => {
        dispatch(setProperty('youTubeUploadDialogOpen', false));
    }
}

const uploadExportToYouTube = (exportId, settings) => {
    return (dispatch) => {
        dispatch(setProperty('isUploadingToYouTube', true));
        axios.post('api/YouTube/Upload', { settings, exportId }).then(response => {
            dispatch(closeYouTubeUploadDialog());

            dispatch(appOperations.openSnack('Upload Succesful! Visit your YouTube channel to see the uploaded video.', ColoredSnackbarConstants.SNACKBAR_SUCCESS));
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => uploadExportToYouTube(exportId, settings), 'Upload to YouTube failed'));
        }).finally(() => {
            dispatch(setProperty('isUploadingToYouTube', false));
        });
    }
}

const changeYouTubeChannel = () => {
    return (dispatch) => {
        axios.get('api/YouTube/ChangeYouTubeChannel').then(response => {
            window.open(response.data.authUrl);
        }).catch(error => {
            dispatch(appOperations.failRequest(error, () => changeYouTubeChannel(), 'YouTube channel change failed'));
        });
    }
}

export default {
    setProperty,
    setExportStatus,
    manageVideoPreset,
    updateExportProgress,
    loadQueuePositions,
    loadVideoExports,
    downloadVideoFile,
    deleteVideoExport,
    removeExportFromList,
    changeVideoPublicStatus,
    sharePublicVideo,
    uploadExportToYouTube,
    changeYouTubeChannel,
    closeYouTubeUploadDialog
};